import React, { useState, useEffect } from 'react';
import { Settings, LogOut, Search, Filter, Plus, UserPlus, HeartPulse, Bed, Activity, ActivitySquare, AlertCircle, Clock, ChevronDown, ChevronUp, UserCheck, Stethoscope, ArrowRight, ArrowLeft, Home, Sparkles, Edit2, X, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import * as storageService from '../services/storageService';
import * as cloudflareService from '../services/cloudflareService';
import { Patient, Room, Hospital, PatientStatus, SurgerySubStatus, SurgeryCondition, STATUS_CONFIG, SUB_STATUS_CONFIG } from '../types';
import * as geminiService from '../services/geminiService';
import { exportBeautifulPDF } from '../utils/exportBeautifulPDF';
import RoomCard from '../components/RoomCard';
import { motion, AnimatePresence } from 'framer-motion';
import { usePatientsListSync } from '../hooks/usePatientsListSync';
import { useRoomsListSync } from '../hooks/useRoomsListSync';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useBatchQueue } from '../hooks/useBatchQueue';
import { AdminNavbarUser } from '../components/AdminNavbarUser';
import BugReportWidget from '../components/BugReportWidget';

const StaffDashboard = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const authData = storageService.getAuthData();
  const hospitalId = authData?.hospitalId;

  const { data: syncedPatients } = usePatientsListSync(hospitalId);
  const { data: syncedRooms } = useRoomsListSync(hospitalId);
  const patients: Patient[] = syncedPatients || [];
  const rooms: Room[] = syncedRooms || [];

  const { queueUpdate } = useBatchQueue(hospitalId || 'H1');

  const { data: historyPatients = [] } = useQuery({
    queryKey: ['hospitalHistory', hospitalId],
    queryFn: () => cloudflareService.getHospitalHistory(hospitalId!),
    enabled: !!hospitalId,
  });

  const [hospitalName, setHospitalName] = useState('Hospital');
  const [hospital, setHospital] = useState<Hospital | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('ALL');
  const [expandedPatientId, setExpandedPatientId] = useState<string | null>(null);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const [patientName, setPatientName] = useState('');
  const [procedureName, setProcedureName] = useState('');
  const [doctorName, setDoctorName] = useState('');

  // Edit Patient Modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingPatientId, setEditingPatientId] = useState<string | null>(null);
  const [editPatientName, setEditPatientName] = useState('');
  const [editProcedureName, setEditProcedureName] = useState('');
  const [editDoctorName, setEditDoctorName] = useState('');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  // Dark Mode State
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Premium Loading State
  const [isAdmitting, setIsAdmitting] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);

  useEffect(() => {
    if (authData) {
      setHospitalName(authData.hospitalName);
      storageService.getHospitalById(authData.hospitalId).then(setHospital);

      // Load Dark Mode Preference
      const savedDarkMode = localStorage.getItem(`staff_dark_mode_${authData.hospitalId}`);
      if (savedDarkMode !== null) {
        setIsDarkMode(savedDarkMode === 'true');
      } else {
        const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
        setIsDarkMode(prefersDark);
      }
    }
  }, []);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    const newVal = !isDarkMode;
    setIsDarkMode(newVal);
    const authData = storageService.getAuthData();
    localStorage.setItem(`staff_dark_mode_${authData?.hospitalId || 'default'}`, String(newVal));
  };

  // Helper function to force reload data (useful after operations)
  const loadData = () => {
    if (hospitalId) {
      queryClient.invalidateQueries({ queryKey: ['patients', hospitalId] });
      queryClient.invalidateQueries({ queryKey: ['rooms', hospitalId] });
    }
  };

  const handleLogout = async () => {
    await storageService.logout();
    navigate('/logout');
  };

  const handleRoomClick = (room: Room, patientId?: string) => {
    if (!room.currentPatientId && !patientId) {
      // Si la habitación está vacía, abrir modal de admisión y pre-seleccionar sala
      setSelectedRoomId(room.id);
      setPatientName('');
      setProcedureName('');
      setDoctorName('');
      setIsModalOpen(true);
    } else if (patientId && !room.currentPatientId) {
      // "Ghost patient" stuck without a valid currentPatientId on the room
      navigate(`/staff/patient/${patientId}`);
    } else {
      // Navigate to patient control
      navigate(`/staff/room/${room.id}`);
    }
  };

  const handleAdmit = async () => {
    if (!selectedRoomId || !patientName.trim()) return;

    const roomId = selectedRoomId;
    const name = patientName.trim();
    const procedure = procedureName.trim();
    const doctor = doctorName.trim();

    // Start Premium Labor Illusion
    setIsAdmitting(true);
    setLoadingStep(0);

    // Text rotation logic (every 600ms)
    const intervalId = setInterval(() => {
      setLoadingStep(prev => (prev < 3 ? prev + 1 : prev));
    }, 600);

    try {
      // Intentionally delay to show off the premium animation (min 2 seconds)
      const submitPromise = storageService.admitPatient(roomId, name, procedure, doctor);
      const delayPromise = new Promise(resolve => setTimeout(resolve, 2400));

      await Promise.all([submitPromise, delayPromise]);

      // Success step
      setLoadingStep(4);

      // Wait a tiny bit more to show the success checkmark
      setTimeout(() => {
        clearInterval(intervalId);
        setIsModalOpen(false);
        setIsAdmitting(false);
        setPatientName('');
        setProcedureName('');
        setDoctorName('');
        setSelectedRoomId(null);
        setPrivacyAccepted(false);

        // Optimistic: mark room as occupied immediately ensures snappiness after modal closes
        queryClient.setQueryData(['rooms', hospitalId], (prevRooms: Room[]) => 
            prevRooms ? prevRooms.map(r => r.id === roomId ? { ...r, currentPatientId: '__pending__' } : r) : []
        );
      }, 800);

    } catch (error) {
      console.error('Error admitting patient:', error);
      clearInterval(intervalId);
      setIsAdmitting(false);
      setLoadingStep(0);
      alert('Hubo un error al admitir al paciente. Intente nuevamente.');
    }
  };

  const handleEditClick = (patient: Patient) => {
    setEditingPatientId(patient.id);
    setEditPatientName(patient.name || '');
    setEditProcedureName(patient.procedureName || '');
    setEditDoctorName(patient.doctorName || '');
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingPatientId) return;

    const updates: Partial<Patient> = {
      name: editPatientName,
      procedureName: editProcedureName,
      doctorName: editDoctorName
    };
    const targetId = editingPatientId;

    // Check if patient is archived (only in historyPatients, not active)
    const isArchived = !patients.find(p => p.id === targetId) && historyPatients.find(p => p.id === targetId);

    if (isArchived) {
      // Direct API call for archived patients — batch queue can't find them locally
      try {
        await cloudflareService.dbUpdatePatient(targetId, updates);
        queryClient.invalidateQueries(['hospitalHistory', hospitalId]);
      } catch (e: any) {
        console.error(e);
        alert('Error actualizando paciente archivado: ' + (e?.message || String(e)));
      }
    } else {
      // Optimistic array mapping via useBatchQueue for active patients
      queueUpdate(targetId, updates).then(() => {
        setTimeout(() => queryClient.invalidateQueries(['hospitalHistory', hospitalId]), 500);
      }).catch(e => {
        console.error(e);
        alert('Error actualizando paciente: ' + (e?.message || String(e)));
      });
    }
    
    setIsEditModalOpen(false);
    setEditingPatientId(null);
  };

  const handleDeletePatient = async (patientId: string) => {
    if (window.confirm('¿Está seguro de que desea eliminar este registro? Esta acción es irreversible.')) {
      // Optimistic: remove patient from state immediately
      const previousPatients = patients;
      queryClient.setQueryData(['patients', hospitalId], (prevPatients: Patient[]) => 
        prevPatients ? prevPatients.filter(p => p.id !== patientId) : []
      );
      setExpandedPatientId(null);
      // Also free the room if assigned
      const deletedPatient = previousPatients.find(p => p.id === patientId);
      if (deletedPatient?.room && deletedPatient.currentStatus !== PatientStatus.EXIT) {
        queryClient.setQueryData(['rooms', hospitalId], (prevRooms: Room[]) => 
          prevRooms ? prevRooms.map(r => r.id === deletedPatient.room ? { ...r, currentPatientId: null } : r) : []
        );
      }

      try {
        // For deleting patient, we can directly call API
        await storageService.deletePatient(patientId);
        queryClient.invalidateQueries(['hospitalHistory', hospitalId]);
        // SSE will confirm the deletion for active patients
      } catch (e) {
        console.error(e);
        alert('Error deleting patient');
      }
    }
  };

  // Merge active patients with history, deduplicate by ID (active takes priority)
  const mergedForBitacora = React.useMemo(() => {
    const seenIds = new Set<string>();
    const merged: Patient[] = [];
    for (const p of patients) { seenIds.add(p.id); merged.push(p); }
    for (const p of historyPatients) { if (!seenIds.has(p.id)) merged.push(p); }
    return merged;
  }, [patients, historyPatients]);

  const filteredPatients = mergedForBitacora.filter(p => {
    const matchesSearch =
      (p.name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      (p.id.toLowerCase() || '').includes(searchQuery.toLowerCase());
    const matchesStatus = selectedStatus === 'ALL' || p.currentStatus === selectedStatus;
    return matchesSearch && matchesStatus;
  }).sort((a, b) => new Date(b.admissionTime).getTime() - new Date(a.admissionTime).getTime());






  const handleExportCSV = () => {
    const headers = ['ID', 'Nombre', 'Procedimiento', 'Doctor', 'Estado Actual', 'Fecha de Ingreso'].join(',');

    const rows = patients.map(p => {
      let date = 'Fecha inválida';
      try {
        if (p.admissionTime) date = new Date(p.admissionTime).toLocaleString();
      } catch (e) { console.warn("Invalid date for patient", p.id); }

      const cleanName = typeof p.name === 'string' ? p.name.replace(/,/g, ' ') : '';
      const cleanProc = typeof p.procedureName === 'string' ? p.procedureName.replace(/,/g, ' ') : '';
      const cleanDoctor = typeof p.doctorName === 'string' ? p.doctorName.replace(/,/g, ' ') : '';
      const cleanStatus = typeof p.currentStatus === 'string' ? p.currentStatus.replace(/,/g, ' ') : '';

      return `"${p.id}","${cleanName}","${cleanProc}","${cleanDoctor}","${cleanStatus}","${date}"`;
    }).join('\n');

    const csvContent = `${headers}\n${rows}`;

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const filename = `log_pacientes_${hospital?.handle || 'hospital'}_${new Date().toISOString().split('T')[0]}.csv`;

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportSinglePatientCSV = (patient: Patient) => {
    const now = new Date();
    const generatedAt = now.toLocaleString('es-MX', { dateStyle: 'long', timeStyle: 'short' });

    // Basic Info Section
    const basicInfoHeaders = ['ID', 'Nombre', 'Procedimiento', 'Doctor', 'Ingreso', 'Alta'].join(',');
    const cleanName = typeof patient.name === 'string' ? patient.name.replace(/,/g, ' ') : '';
    const cleanProc = typeof patient.procedureName === 'string' ? patient.procedureName.replace(/,/g, ' ') : '';
    const cleanDoctor = typeof patient.doctorName === 'string' ? patient.doctorName.replace(/,/g, ' ') : '';
    
    let admissionTimeStr = '---';
    if (patient.admissionTime) {
      try { admissionTimeStr = new Date(patient.admissionTime).toLocaleString('es-MX', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch(e){}
    }
    let dischargeTimeStr = '---';
    if (patient.dischargeTime) {
      try { dischargeTimeStr = new Date(patient.dischargeTime).toLocaleString('es-MX', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch(e){}
    }
    
    const basicInfoRow = `"${patient.id}","${cleanName}","${cleanProc}","${cleanDoctor}","${admissionTimeStr}","${dischargeTimeStr}"`;

    // Logs Section
    const logHeaders = ['Fecha y Hora', 'Estado', 'Sub-Estado', 'Condición'].join(',');
    const logRows = (patient.logs || []).map(log => {
      let timeStr = '---';
      if (log.timestamp) {
        try { timeStr = new Date(log.timestamp).toLocaleString('es-MX', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }); } catch(e){}
      }
      const stLabel = STATUS_CONFIG[log.status as keyof typeof STATUS_CONFIG]?.label || log.status;
      const subLabel = SUB_STATUS_CONFIG[log.subStatus as keyof typeof SUB_STATUS_CONFIG]?.label || log.subStatus;
      const condLabel = typeof log.condition === 'string' ? log.condition.replace(/_/g, ' ') : '';
      return `"${timeStr}","${stLabel}","${subLabel}","${condLabel}"`;
    }).join('\n');

    // Calculate Station Breakdown for CSV
    const sortedLogs = [...(patient.logs || [])].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    const stations = ['WAITING', 'PREPARATION', 'ROOM_ENTRY', 'ANESTHESIA', 'SURGERY', 'RECOVERY'];
    const calcBreakdown: any[] = [];
    
    for (let i = 0; i < sortedLogs.length; i++) {
        const log = sortedLogs[i];
        if (!stations.includes(log.status)) continue;
        if (calcBreakdown.some(b => b.station === log.status)) continue;
        
        const startTime = new Date(log.timestamp).getTime();
        const nextDiffLog = sortedLogs.slice(i + 1).find(l => l.status !== log.status && stations.includes(l.status));
        
        let endTime;
        if (nextDiffLog) {
            endTime = new Date(nextDiffLog.timestamp).getTime();
        } else {
            const exitLog = sortedLogs.find(l => l.status === 'EXIT');
            if (exitLog) {
                endTime = new Date(exitLog.timestamp).getTime();
            } else if (patient.dischargeTime) {
                endTime = new Date(patient.dischargeTime).getTime();
            } else {
                endTime = new Date().getTime();
            }
        }
        const durationMin = Math.round((endTime - startTime) / 60000);
        calcBreakdown.push({ station: log.status, durationMin });
    }
    
    stations.forEach(st => {
        if (!calcBreakdown.some(b => b.station === st)) calcBreakdown.push({ station: st, durationMin: null });
    });
    calcBreakdown.sort((a,b) => stations.indexOf(a.station) - stations.indexOf(b.station));

    const STATION_LABELS: Record<string, string> = {
        WAITING: 'En Espera', PREPARATION: 'Preparación', ROOM_ENTRY: 'Ingreso a Qx',
        ANESTHESIA: 'Anestesia', SURGERY: 'Cirugía', RECOVERY: 'Recuperación',
    };
    
    const breakdownHeaders = ['Estación', 'Duración (min)'].join(',');
    const breakdownRows = calcBreakdown
        .filter(s => s.durationMin !== null)
        .map(s => `"${STATION_LABELS[s.station] || s.station}","${s.durationMin}"`)
        .join('\n');

    const csvContent = `"HOSPITAL STATUS - REPORTE DE PACIENTE"\n"Generado el: ${generatedAt}"\n\n` +
      `"[Datos del Paciente]"\n${basicInfoHeaders}\n${basicInfoRow}\n\n` +
      `"[Tiempos por Estación]"\n${breakdownHeaders}\n${breakdownRows}\n\n` +
      `"[Historial de Movimientos]"\n${logHeaders}\n${logRows}\n\n` +
      `"Documento generado por el sistema automatizado Hospital Status."`;

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const filename = `Reporte_${cleanName || patient.id.slice(0,8)}_${new Date().toISOString().split('T')[0]}.csv`;

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className={`min-h-screen font-sans selection:bg-indigo-500/30 transition-colors duration-500 ${isDarkMode ? 'bg-[#0a0c10] text-slate-200' : 'bg-[#F8FAFC] text-slate-800'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-30 transition-all ${isDarkMode ? 'bg-[#0a0c10]/80 border-white/10' : 'bg-white/80 border-slate-200'} backdrop-blur-xl border-b relative overflow-hidden`}>
        {/* Dynamic Brand Glow */}
        <div
          className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none"
          style={{ background: `linear-gradient(90deg, ${hospital?.branding?.primaryColor || '#5B65DC'}, transparent)` }}
        />
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between relative z-10">
          <div className="flex items-center gap-6">
            <button
              onClick={() => navigate('/staff')}
              className={`p-2.5 rounded-xl transition-all ${isDarkMode ? 'text-white/50 hover:text-white hover:bg-white/10' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'}`}
              title="Volver al menú principal"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              {hospital?.branding?.logoDarkUrl && isDarkMode && hospital.branding.logoDarkUrl.trim() !== '' && hospital.branding.logoDarkUrl !== 'null' ? (
                <div className={`h-10 w-auto flex items-center justify-center p-1 bg-white/5 backdrop-blur-sm rounded-lg border border-white/10 shadow-sm shrink-0`} style={{ boxShadow: `0 0 15px ${hospital?.branding?.primaryColor || '#5B65DC'}30` }}>
                  <img
                    src={hospital.branding.logoDarkUrl}
                    alt={hospitalName}
                    className="h-full w-auto object-contain filter drop-shadow-sm"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                      (e.target as HTMLImageElement).parentElement!.className = "hidden";
                    }}
                  />
                </div>
              ) : hospital?.branding?.logoUrl && hospital.branding.logoUrl.trim() !== '' && hospital.branding.logoUrl !== 'null' ? (
                <div className={`h-10 w-auto flex items-center justify-center p-1 ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'} backdrop-blur-sm rounded-lg border shadow-sm shrink-0`} style={{ boxShadow: `0 0 15px ${hospital?.branding?.primaryColor || '#5B65DC'}30` }}>
                  <img
                    src={hospital.branding.logoUrl}
                    alt={hospitalName}
                    className="h-full w-auto object-contain filter drop-shadow-sm"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                      (e.target as HTMLImageElement).parentElement!.className = "hidden";
                    }}
                  />
                </div>
              ) : (
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg" style={{ backgroundColor: hospital?.branding?.primaryColor || '#5B65DC', boxShadow: `0 0 20px ${hospital?.branding?.primaryColor || '#5B65DC'}80` }}>
                  <HeartPulse className="w-5 h-5 text-white" />
                </div>
              )}
              <div>
                <h1 className={`text-xl font-black tracking-tight`} style={{ color: isDarkMode ? '#ffffff' : (hospital?.branding?.primaryColor || '#0f172a') }}>{hospitalName}</h1>
                <p className={`text-[10px] font-bold uppercase tracking-widest mt-0.5`} style={{ color: isDarkMode ? (hospital?.branding?.primaryColor || '#818cf8') : (hospital?.branding?.secondaryColor || '#4f46e5') }}>Portal Médico</p>
              </div>
            </div>


          </div>

          <div className="flex items-center gap-3">
            <div className="hidden md:block mr-2">
               <AdminNavbarUser primaryColor={hospital?.branding?.primaryColor} theme={isDarkMode ? 'dark' : 'light'} />
            </div>
            <button
              onClick={toggleDarkMode}
              className={`p-2.5 rounded-xl transition-all ${isDarkMode ? 'bg-white/5 text-yellow-400 hover:bg-white/10' : 'bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-900'}`}
              title={isDarkMode ? "Modo Claro" : "Modo Oscuro"}
            >
              {isDarkMode ? (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>
            <div className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full border ${isDarkMode ? 'bg-[#181b21] border-white/10 text-emerald-400' : 'bg-emerald-50 border-emerald-100/50 text-emerald-600'}`}>
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
              <span className="text-[10px] font-bold uppercase tracking-widest">Sistema Activo</span>
            </div>
            <button
              onClick={handleLogout}
              className={`p-2.5 rounded-xl transition-all ${isDarkMode ? 'text-white/40 hover:text-white hover:bg-white/5' : 'text-slate-400 hover:text-red-500 hover:bg-red-50'}`}
              title="Cerrar Sesión"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">

        {/* Top Actions & Quick Stats */}
        <div className="flex flex-col md:flex-row gap-6 justify-between items-start md:items-end mb-10">
          <div>
            <h2 className={`text-3xl font-black tracking-tight mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Panel Médico</h2>
            <p className={`${isDarkMode ? 'text-white/50' : 'text-slate-500'} font-medium`}>Vista general de quirófanos e historial de pacientes.</p>
          </div>

          <div className="flex flex-wrap gap-3">


            <button
              onClick={() => {
                setPatientName('');
                setProcedureName('');
                setDoctorName('');
                setSelectedRoomId(null);
                setPrivacyAccepted(false);
                setIsModalOpen(true);
              }}
              className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold rounded-xl shadow-lg shadow-indigo-600/20 transition-all hover:-translate-y-0.5"
              style={{ backgroundColor: hospital?.branding?.primaryColor || '#5B65DC', boxShadow: `0 4px 14px 0 ${hospital?.branding?.primaryColor || '#5B65DC'}40` }}
            >
              <Plus className="w-4 h-4" />
              Nueva Admisión
            </button>
          </div>
        </div>


        {/* Room Grid */}
        <section className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h3 className={`text-lg font-black tracking-tight flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              <Stethoscope className={`w-5 h-5 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
              Control de Quirófanos
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {rooms.length === 0 ? (
              <div className="col-span-full py-12 text-center">
                <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-[#181b21] border border-white/10' : 'bg-white border border-slate-100'}`}>
                  <ActivitySquare className={`w-8 h-8 ${isDarkMode ? 'text-white/20' : 'text-slate-300'}`} />
                </div>
                <h3 className={`text-lg font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>No hay áreas configuradas</h3>
                <p className={`text-sm ${isDarkMode ? 'text-white/40' : 'text-slate-500'}`}>Contacte al administrador para configurar los quirófanos.</p>
              </div>
            ) : (
              rooms.map((room) => {
                const patient = patients.find(p => p.id === room.currentPatientId)
                  || patients.find(p => p.room === room.id && p.currentStatus !== PatientStatus.EXIT && p.currentStatus !== PatientStatus.RECOVERY);
                return (
                  <RoomCard
                    key={room.id}
                    room={room}
                    patientStatus={patient?.currentStatus}
                    patientCode={patient?.id}
                    onClick={() => handleRoomClick(room, patient?.id)}
                    primaryColor={hospital?.branding?.primaryColor}
                    isDarkMode={isDarkMode}
                  />
                );
              })
            )}
          </div>
        </section>

        {/* Recovery Section */}
        {patients.filter(p => p.currentStatus === PatientStatus.RECOVERY).length > 0 && (
          <section className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <h3 className={`text-lg font-black tracking-tight flex items-center gap-2 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                <Activity className="w-5 h-5" />
                Pacientes en Recuperación
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {patients.filter(p => p.currentStatus === PatientStatus.RECOVERY).map(patient => (
                <div
                  key={patient.id}
                  onClick={() => navigate(`/staff/patient/${patient.id}`)}
                  className={`p-5 sm:p-6 rounded-3xl border flex flex-col justify-between cursor-pointer transition-all hover:-translate-y-1 hover:shadow-lg ${isDarkMode ? 'bg-[#181b21] border-emerald-500/20 hover:border-emerald-500/40' : 'bg-white border-emerald-200 shadow-sm hover:border-emerald-300'}`}
                >
                  <div>
                    <div className="flex justify-between items-start mb-2">
                      <h4 className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{patient.name || patient.id}</h4>
                      <div className={`p-2 rounded-xl bg-opacity-20 ${isDarkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-600'}`}>
                        <HeartPulse className="w-4 h-4 animate-pulse" />
                      </div>
                    </div>
                    <p className={`text-xs font-medium mb-4 ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>{patient.procedureName || 'Sin procedimiento registrado'}</p>
                    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] uppercase font-bold tracking-widest ${isDarkMode ? 'bg-emerald-500/10 text-emerald-300' : 'bg-emerald-50 text-emerald-600'}`}>
                      <Activity className="w-3 h-3" /> Recuperación
                    </div>
                  </div>

                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (window.confirm(`¿Dar de alta al paciente ${patient.name || patient.id}?`)) {
                        const pid = patient.id;
                        // Optimistic: update state immediately
                        queryClient.setQueryData(['patients', hospitalId], (prevPatients: Patient[]) => 
                          prevPatients ? prevPatients.map(p => p.id === pid ? { ...p, currentStatus: PatientStatus.EXIT, dischargeTime: new Date().toISOString() } : p) : []
                        );
                        queryClient.setQueryData(['rooms', hospitalId], (prevRooms: Room[]) => 
                          prevRooms ? prevRooms.map(r => r.currentPatientId === pid ? { ...r, currentPatientId: null } : r) : []
                        );
                        try {
                          await storageService.updatePatientStatus(pid, PatientStatus.EXIT);
                          // SSE will confirm
                        } catch (e) {
                          console.error(e);
                          alert('Error al dar de alta al paciente');
                          // Rollback on failure
                          loadData();
                        }
                      }
                    }}
                    className={`mt-6 w-full py-3 rounded-xl flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-wider transition-all ${isDarkMode ? 'bg-white/5 hover:bg-emerald-500/20 text-white hover:text-emerald-400 border border-white/10 hover:border-emerald-500/30' : 'bg-slate-50 hover:bg-emerald-50 text-slate-700 hover:text-emerald-700 border border-slate-200 hover:border-emerald-200'}`}
                  >
                    <LogOut className="w-4 h-4" />
                    Dar de Alta (Salida)
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Patient Database / Log */}
        <section>
          <div className={`rounded-3xl border overflow-hidden shadow-sm ${isDarkMode ? 'bg-[#181b21] border-white/10' : 'bg-white border-slate-200'}`}>
            <div className={`p-6 border-b flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`}>
              <div className="flex items-center gap-3">
                <div className={`p-2.5 rounded-xl ${isDarkMode ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-50 text-indigo-600'}`}>
                  <UserPlus className="w-5 h-5" />
                </div>
                <div>
                  <h3 className={`text-lg font-black ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Historial Médico</h3>
                  <p className={`text-xs font-medium ${isDarkMode ? 'text-white/40' : 'text-slate-500'}`}>Últimos 90 días · Incluye pacientes dados de alta</p>
                </div>
              </div>

              <div className="flex items-center gap-3 w-full sm:w-auto">
                <div className="relative flex-1 sm:w-64">
                  <Search className={`w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-white/30' : 'text-slate-400'}`} />
                  <input
                    type="text"
                    placeholder="Buscar por ID o nombre..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={`w-full pl-9 pr-4 py-2.5 rounded-xl text-sm font-medium border focus:ring-2 outline-none transition-all ${isDarkMode ? 'bg-[#111318] border-white/10 text-white placeholder:text-white/20 focus:border-indigo-500 focus:ring-indigo-500/20' : 'bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-indigo-500 focus:ring-indigo-100'}`}
                  />
                </div>
                <button
                  onClick={handleExportCSV}
                  className={`p-2.5 border rounded-xl transition-colors ${isDarkMode ? 'border-white/10 text-white/40 hover:text-white hover:bg-white/5' : 'border-slate-200 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 hover:border-indigo-200'}`}
                  title="Exportar a CSV"
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </div>

            {filteredPatients.length === 0 ? (
              <div className="p-12 text-center">
                <UserPlus className={`w-12 h-12 mx-auto mb-4 ${isDarkMode ? 'text-white/10' : 'text-slate-200'}`} />
                <p className={`text-sm font-bold ${isDarkMode ? 'text-white/40' : 'text-slate-500'}`}>No se encontraron pacientes en el historial</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className={`text-[10px] uppercase tracking-widest border-b ${isDarkMode ? 'text-white/30 border-white/10 bg-[#111318]/50' : 'text-slate-400 border-slate-100 bg-slate-50/50'}`}>
                      <th className="px-6 py-4 font-black">ID Paciente</th>
                      <th className="px-6 py-4 font-black">Nombre</th>
                      <th className="px-6 py-4 font-black">Procedimiento</th>
                      <th className="px-6 py-4 font-black">Doctor</th>
                      <th className="px-6 py-4 font-black">Estado Último</th>
                      <th className="px-6 py-4 font-black text-right">Ingreso</th>
                      <th className="px-6 py-4 font-black text-right"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <AnimatePresence>
                      {filteredPatients.map((patient) => {
                        const isExpanded = expandedPatientId === patient.id;
                        return (
                          <React.Fragment key={patient.id}>
                            <motion.tr
                              layout
                              onClick={() => setExpandedPatientId(isExpanded ? null : patient.id)}
                              className={`group cursor-pointer transition-colors border-b ${isDarkMode ? 'hover:bg-white/[0.02] border-white/5' : 'hover:bg-slate-50 border-slate-50'} ${isExpanded ? (isDarkMode ? 'bg-white/[0.02]' : 'bg-slate-50') : ''}`}
                            >
                              <td className="px-6 py-4">
                                <span className={`font-mono text-xs font-bold ${isDarkMode ? 'text-white/80' : 'text-slate-900'}`}>
                                  {patient.id.slice(0, 8)}...
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-2">
                                  {patient.name ? (
                                    <span className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                      {patient.name}
                                    </span>
                                  ) : (
                                    <span className={`text-sm italic ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
                                      Anónimo
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <span className={`text-sm font-medium truncate max-w-[200px] block ${isDarkMode ? 'text-white/60' : 'text-slate-600'}`}>
                                  {patient.procedureName || '---'}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <span className={`text-sm font-medium truncate max-w-[200px] block ${isDarkMode ? 'text-white/60' : 'text-slate-600'}`}>
                                  {patient.doctorName || '---'}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-wider border ${patient.currentStatus === 'DISCHARGED' || patient.currentStatus === 'RECOVERY' || patient.currentStatus === 'EXIT'
                                  ? (isDarkMode ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-emerald-50 text-emerald-600 border-emerald-100')
                                  : patient.currentStatus === 'SURGERY'
                                    ? (isDarkMode ? 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20' : 'bg-indigo-50 text-indigo-600 border-indigo-100')
                                    : (isDarkMode ? 'bg-[#252830] text-white/50 border-white/10' : 'bg-slate-100 text-slate-500 border-slate-200')
                                  }`}>
                                  {patient.currentStatus ? patient.currentStatus.replace('_', ' ') : 'SIN ESTADO'}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-right">
                                <span className={`font-mono text-xs font-medium ${isDarkMode ? 'text-white/40' : 'text-slate-500'}`}>
                                  {new Date(patient.admissionTime).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-right">
                                <button className={`p-1.5 rounded-full transition-colors ${isDarkMode ? 'text-white/30 hover:text-white hover:bg-white/10' : 'text-slate-400 hover:text-indigo-600 hover:bg-indigo-50'}`}>
                                  {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                </button>
                              </td>
                            </motion.tr>
                            {/* Expanded Content: Patient Timeline */}
                            {isExpanded && (
                              <tr className={`border-b shadow-[inset_0_4px_6px_-4px_rgba(0,0,0,0.05)] ${isDarkMode ? 'bg-[#111318] border-white/5' : 'bg-slate-50/50 border-slate-100/60'}`}>
                                <td colSpan={7} className="px-6 py-6">
                                  <div className={`pl-4 border-l-2 ml-2 space-y-4 ${isDarkMode ? 'border-white/10' : 'border-indigo-100/50'}`}>
                                    <p className={`text-[10px] font-black uppercase tracking-widest mb-2 ${isDarkMode ? 'text-white/30' : 'text-slate-400'}`}>Historial de Movimientos</p>
                                    {patient.logs && patient.logs.length > 0 ? (
                                      patient.logs.map((log, index) => (
                                        <div key={index} className="flex items-start gap-4 animate-fade-in" style={{ animationDelay: `${index * 0.05}s` }}>
                                          <div className={`p-1.5 rounded-full shadow-sm border mt-0.5 z-10 -translate-x-[29px] ${isDarkMode ? 'bg-[#181b21] border-white/20 text-white/50' : 'bg-white border-slate-200 text-slate-400'}`}>
                                            <Clock className="w-3 h-3" />
                                          </div>
                                          <div className="-ml-3">
                                            <div className="flex items-center gap-2">
                                              <span className={`text-xs font-bold uppercase tracking-wide px-2 py-0.5 rounded border shadow-sm ${isDarkMode ? 'bg-[#252830] text-white border-white/10' : 'bg-white text-indigo-900 border-slate-200'}`}>{log.status.replace('_', ' ')}</span>
                                              <span className={`font-mono text-[10px] ${isDarkMode ? 'text-white/40' : 'text-slate-500'}`}>{new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
                                            </div>
                                            {(log.subStatus || log.condition) && (
                                              <div className="mt-1 flex gap-2">
                                                {log.subStatus && <span className={`text-[10px] ${isDarkMode ? 'text-white/30' : 'text-slate-500'}`}>→ {log.subStatus}</span>}
                                                {log.condition && <span className={`text-[10px] font-medium ${log.condition === 'NORMAL' ? 'text-emerald-500' : 'text-amber-500'}`}>({log.condition})</span>}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      ))
                                    ) : (
                                      <p className={`text-xs italic ${isDarkMode ? 'text-white/30' : 'text-slate-400'}`}>No hay movimientos registrados.</p>
                                    )}

                                    <div className="pt-4 mt-6 border-t flex items-center justify-between border-slate-100">
                                      <div className="text-[10px] text-slate-400 font-medium italic pl-1">
                                        {patient.lastEditedAt && (
                                          <span>Última edición manual: {new Date(patient.lastEditedAt).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</span>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <motion.button
                                          whileHover={{ scale: 1.05 }}
                                          whileTap={{ scale: 0.95 }}
                                          onClick={() => exportBeautifulPDF(patient)}
                                          className={`text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-lg border transition-colors ${isDarkMode ? 'text-indigo-400 border-indigo-500/20 hover:bg-indigo-500/10' : 'text-indigo-600 border-indigo-100 hover:bg-indigo-50'}`}
                                        >
                                          PDF
                                        </motion.button>
                                        <motion.button
                                          whileHover={{ scale: 1.05 }}
                                          whileTap={{ scale: 0.95 }}
                                          onClick={() => handleExportSinglePatientCSV(patient)}
                                          className={`text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-lg border transition-colors ${isDarkMode ? 'text-slate-400 border-slate-500/20 hover:bg-slate-500/10' : 'text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                                        >
                                          CSV
                                        </motion.button>
                                        <motion.button
                                          whileHover={{ scale: 1.05 }}
                                          whileTap={{ scale: 0.95 }}
                                          onClick={() => handleEditClick(patient)}
                                          className={`text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-lg border transition-colors ${isDarkMode ? 'text-indigo-400 border-indigo-500/20 hover:bg-indigo-500/10' : 'text-indigo-600 border-indigo-100 hover:bg-indigo-50'}`}
                                        >
                                          Editar
                                        </motion.button>
                                        <motion.button
                                          whileHover={{ scale: 1.05 }}
                                          whileTap={{ scale: 0.95 }}
                                          onClick={() => handleDeletePatient(patient.id)}
                                          className={`text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-lg border transition-colors ${isDarkMode ? 'text-red-400 border-red-500/20 hover:bg-red-500/10' : 'text-red-600 border-red-100 hover:bg-red-50'}`}
                                        >
                                          Eliminar
                                        </motion.button>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        {/* Discrete Bug Report Footer */}
        <div className={`text-center py-4 mt-6 ${isDarkMode ? 'border-t border-white/5' : 'border-t border-slate-100'}`}>
          <BugReportWidget isDarkMode={isDarkMode} hospitalName={hospitalName} />
        </div>
      </main>

      {/* Admission Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/60 backdrop-blur-md"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className={`w-full max-w-sm rounded-[2.5rem] shadow-2xl overflow-hidden border ${isDarkMode ? 'bg-[#181b21] border-white/10 shadow-black/50' : 'bg-white border-slate-200'}`}
            >
              <div className={`p-8 border-b flex justify-between items-center ${isDarkMode ? 'bg-[#1e2128] border-white/5' : 'bg-slate-50/50 border-slate-100'}`}>
                <h3 className={`text-lg font-black tracking-tight ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Nueva Admisión</h3>
                <button onClick={() => { setIsModalOpen(false); setSelectedRoomId(null); }} className={`p-2 rounded-full transition-all border border-transparent shadow-sm ${isDarkMode ? 'hover:bg-white/5 hover:border-white/10' : 'hover:bg-white hover:border-slate-100'}`}>
                  <X className={`w-5 h-5 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`} />
                </button>
              </div>
              <div className="p-8">
                <AnimatePresence mode="wait">
                  {!isAdmitting ? (
                    <motion.div
                      key="form"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className={`border p-5 rounded-3xl mb-8 space-y-4 shadow-sm ${isDarkMode ? 'bg-[#111318] border-white/5' : 'bg-white border-slate-100'}`}>
                        <div className="flex items-center gap-4">
                          <div className="p-3 rounded-2xl" style={{ backgroundColor: `${hospital?.branding?.primaryColor || '#5B65DC'}1A` }}>
                            <Bed className="w-6 h-6" style={{ color: hospital?.branding?.primaryColor || '#5B65DC' }} />
                          </div>
                          <div>
                            <p className={`text-[10px] font-black uppercase tracking-[0.2em] mb-1 ${isDarkMode ? 'text-white/30' : 'text-slate-400'}`}>Quirófano Asignado</p>
                            <p className={`text-sm font-black ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                              {selectedRoomId ? rooms.find(r => r.id === selectedRoomId)?.name : 'Seleccione una área'}
                            </p>
                          </div>
                        </div>

                        {!selectedRoomId && (
                          <div className="grid grid-cols-2 gap-2 mt-2">
                            {rooms.filter(r => !r.currentPatientId).map(room => (
                              <button
                                key={room.id}
                                onClick={() => setSelectedRoomId(room.id)}
                                className={`py-3 px-3 text-[10px] font-black border rounded-xl transition-all uppercase tracking-wider ${isDarkMode ? 'border-white/10 hover:border-indigo-500 hover:bg-indigo-500/10 text-white/50' : 'border-slate-100 hover:border-indigo-200 hover:bg-indigo-50 text-slate-600'}`}
                              >
                                {room.name}
                              </button>
                            ))}
                            {rooms.filter(r => !r.currentPatientId).length === 0 && (
                              <p className="col-span-2 text-[10px] text-red-500 font-bold text-center py-2">No hay áreas disponibles</p>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="space-y-6">
                        <div>
                          <label className={`block text-[10px] font-black uppercase tracking-[0.2em] mb-3 ml-1 ${isDarkMode ? 'text-white/30' : 'text-slate-400'}`}>Nombre Paciente</label>
                          <input
                            type="text"
                            value={patientName}
                            onChange={(e) => setPatientName(e.target.value)}
                            placeholder="Ej: Juan Pérez"
                            className={`w-full border-2 border-transparent p-5 rounded-2xl font-bold transition-all outline-none ${isDarkMode ? 'bg-[#111318] focus:border-indigo-500 focus:bg-[#181b21] text-white placeholder:text-white/20' : 'bg-slate-50 focus:border-indigo-500 focus:bg-white text-slate-900 placeholder:text-slate-300'}`}
                            autoFocus
                          />
                        </div>
                        <div>
                          <label className={`block text-[10px] font-black uppercase tracking-[0.2em] mb-3 ml-1 ${isDarkMode ? 'text-white/30' : 'text-slate-400'}`}>Procedimiento</label>
                          <input
                            type="text"
                            value={procedureName}
                            onChange={(e) => setProcedureName(e.target.value)}
                            placeholder="Ej: Artroscopia"
                            className={`w-full border-2 border-transparent p-5 rounded-2xl font-bold transition-all outline-none ${isDarkMode ? 'bg-[#111318] focus:border-indigo-500 focus:bg-[#181b21] text-white placeholder:text-white/20' : 'bg-slate-50 focus:border-indigo-500 focus:bg-white text-slate-900 placeholder:text-slate-300'}`}
                          />
                        </div>
                        <div>
                          <label className={`block text-[10px] font-black uppercase tracking-[0.2em] mb-3 ml-1 ${isDarkMode ? 'text-white/30' : 'text-slate-400'}`}>Doctor</label>
                          <input
                            type="text"
                            value={doctorName}
                            onChange={(e) => setDoctorName(e.target.value)}
                            placeholder="Ej: Dr. García López"
                            className={`w-full border-2 border-transparent p-5 rounded-2xl font-bold transition-all outline-none ${isDarkMode ? 'bg-[#111318] focus:border-indigo-500 focus:bg-[#181b21] text-white placeholder:text-white/20' : 'bg-slate-50 focus:border-indigo-500 focus:bg-white text-slate-900 placeholder:text-slate-300'}`}
                          />
                        </div>

                        {hospital?.preferences?.requirePrivacyNotice && (
                          <label className={`flex items-start gap-3 mt-4 text-[10px] text-left cursor-pointer px-1 ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>
                            <input
                              type="checkbox"
                              checked={privacyAccepted}
                              onChange={(e) => setPrivacyAccepted(e.target.checked)}
                              className="mt-0.5 w-4 h-4 rounded border-slate-300 transition-colors shrink-0"
                              style={{ accentColor: hospital?.branding?.primaryColor || '#5B65DC' }}
                            />
                            <span>
                              Confirmo que el paciente ha leído y aceptado el{' '}
                              <a href="#" className="underline font-bold transition-opacity hover:opacity-80">Aviso de Privacidad</a>{' '}
                              sobre el manejo de sus datos médicos.
                            </span>
                          </label>
                        )}

                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={handleAdmit}
                          disabled={!selectedRoomId || (hospital?.preferences?.requirePrivacyNotice && !privacyAccepted)}
                          className="w-full text-white font-black py-5 rounded-2xl shadow-xl transition-all disabled:opacity-20 disabled:grayscale disabled:cursor-not-allowed uppercase tracking-[0.2em] text-[10px] hover:opacity-90"
                          style={{ backgroundColor: hospital?.branding?.primaryColor || '#122056', boxShadow: `0 20px 25px -5px ${hospital?.branding?.primaryColor || '#122056'}30` }}
                        >
                          Confirmar Ingreso
                        </motion.button>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="loading"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.4, ease: "easeOut" }}
                      className="flex flex-col items-center justify-center py-10 space-y-8"
                    >
                      <div className="relative w-24 h-24 flex items-center justify-center">
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
                          className="absolute inset-0 rounded-full border-4 border-dashed opacity-20"
                          style={{ borderColor: hospital?.branding?.primaryColor || '#5B65DC' }}
                        />
                        <motion.div
                          animate={{ scale: [1, 1.1, 1] }}
                          transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
                          className="w-16 h-16 rounded-full flex items-center justify-center shadow-lg relative z-10"
                          style={{
                            backgroundColor: loadingStep === 4 ? '#10b981' : (hospital?.branding?.primaryColor || '#5B65DC'),
                            boxShadow: `0 0 30px ${loadingStep === 4 ? '#10b981' : (hospital?.branding?.primaryColor || '#5B65DC')}60`
                          }}
                        >
                          <AnimatePresence mode="wait">
                            <motion.div
                              key={loadingStep === 4 ? 'check' : 'pulse'}
                              initial={{ opacity: 0, scale: 0 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0 }}
                              transition={{ duration: 0.3 }}
                            >
                              {loadingStep === 4 ? (
                                <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                <HeartPulse className="w-8 h-8 text-white animate-pulse" />
                              )}
                            </motion.div>
                          </AnimatePresence>
                        </motion.div>
                      </div>

                      <div className="w-full space-y-3 text-center">
                        <AnimatePresence mode="wait">
                          <motion.p
                            key={loadingStep}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className={`text-sm font-black uppercase tracking-widest ${isDarkMode ? 'text-white' : 'text-slate-900'}`}
                          >
                            {loadingStep === 0 && "Verificando credenciales..."}
                            {loadingStep === 1 && "Creando sala médica segura..."}
                            {loadingStep === 2 && "Asignando equipo y doctor..."}
                            {loadingStep === 3 && "Finalizando admisión..."}
                            {loadingStep === 4 && "¡Sala Creada Exitosamente!"}
                          </motion.p>
                        </AnimatePresence>

                        <div className={`h-2 w-full rounded-full overflow-hidden ${isDarkMode ? 'bg-white/10' : 'bg-slate-100'}`}>
                          <motion.div
                            initial={{ width: "0%" }}
                            animate={{
                              width:
                                loadingStep === 0 ? "25%" :
                                  loadingStep === 1 ? "50%" :
                                    loadingStep === 2 ? "75%" :
                                      loadingStep === 3 ? "90%" : "100%"
                            }}
                            transition={{ duration: 0.5, ease: "easeInOut" }}
                            className="h-full rounded-full"
                            style={{ backgroundColor: loadingStep === 4 ? '#10b981' : (hospital?.branding?.primaryColor || '#5B65DC') }}
                          />
                        </div>
                        <p className={`text-[10px] font-bold ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
                          No cierre esta ventana
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Patient Modal */}
      <AnimatePresence>
        {isEditModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/60 backdrop-blur-md"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className={`w-full max-w-sm rounded-[2.5rem] shadow-2xl overflow-hidden border ${isDarkMode ? 'bg-[#181b21] border-white/10 shadow-black/50' : 'bg-white border-slate-200'}`}
            >
              <div className={`p-8 border-b flex justify-between items-center ${isDarkMode ? 'bg-[#1e2128] border-white/5' : 'bg-slate-50/50 border-slate-100'}`}>
                <h3 className={`text-lg font-black tracking-tight ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Editar Paciente</h3>
                <button onClick={() => setIsEditModalOpen(false)} className={`p-2 rounded-full transition-all border border-transparent shadow-sm ${isDarkMode ? 'hover:bg-white/5 hover:border-white/10' : 'hover:bg-white hover:border-slate-100'}`}>
                  <X className={`w-5 h-5 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`} />
                </button>
              </div>
              <div className="p-8 space-y-6">
                <div>
                  <label className={`block text-[10px] font-black uppercase tracking-[0.2em] mb-3 ml-1 ${isDarkMode ? 'text-white/30' : 'text-slate-400'}`}>Nombre Paciente</label>
                  <input
                    type="text"
                    value={editPatientName}
                    onChange={(e) => setEditPatientName(e.target.value)}
                    placeholder="Ej: Juan Pérez"
                    className={`w-full border-2 border-transparent p-5 rounded-2xl font-bold transition-all outline-none ${isDarkMode ? 'bg-[#111318] focus:border-indigo-500 focus:bg-[#181b21] text-white placeholder:text-white/20' : 'bg-slate-50 focus:border-indigo-500 focus:bg-white text-slate-900 placeholder:text-slate-300'}`}
                    autoFocus
                  />
                </div>
                <div>
                  <label className={`block text-[10px] font-black uppercase tracking-[0.2em] mb-3 ml-1 ${isDarkMode ? 'text-white/30' : 'text-slate-400'}`}>Procedimiento</label>
                  <input
                    type="text"
                    value={editProcedureName}
                    onChange={(e) => setEditProcedureName(e.target.value)}
                    placeholder="Ej: Artroscopia"
                    className={`w-full border-2 border-transparent p-5 rounded-2xl font-bold transition-all outline-none ${isDarkMode ? 'bg-[#111318] focus:border-indigo-500 focus:bg-[#181b21] text-white placeholder:text-white/20' : 'bg-slate-50 focus:border-indigo-500 focus:bg-white text-slate-900 placeholder:text-slate-300'}`}
                  />
                </div>
                <div>
                  <label className={`block text-[10px] font-black uppercase tracking-[0.2em] mb-3 ml-1 ${isDarkMode ? 'text-white/30' : 'text-slate-400'}`}>Doctor</label>
                  <input
                    type="text"
                    value={editDoctorName}
                    onChange={(e) => setEditDoctorName(e.target.value)}
                    placeholder="Ej: Dr. García López"
                    className={`w-full border-2 border-transparent p-5 rounded-2xl font-bold transition-all outline-none ${isDarkMode ? 'bg-[#111318] focus:border-indigo-500 focus:bg-[#181b21] text-white placeholder:text-white/20' : 'bg-slate-50 focus:border-indigo-500 focus:bg-white text-slate-900 placeholder:text-slate-300'}`}
                  />
                </div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleSaveEdit}
                  className="w-full text-white font-black py-5 rounded-2xl shadow-xl transition-all uppercase tracking-[0.2em] text-[10px] hover:opacity-90"
                  style={{ backgroundColor: hospital?.branding?.primaryColor || '#122056', boxShadow: `0 20px 25px -5px ${hospital?.branding?.primaryColor || '#122056'}30` }}
                >
                  Guardar Cambios
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StaffDashboard;
